import React, { useMemo } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Box, Typography, Tooltip } from '@mui/material'; // Aggiunto Tooltip
import clsx from 'clsx';
import dayjs from 'dayjs';

// --- CONFIGURAZIONE ORARI ---
const START_HOUR = 7; 
const END_HOUR = 23;

const TIME_SLOTS = Array.from(
    { length: END_HOUR - START_HOUR + 1 }, 
    (_, i) => START_HOUR + i
);

const getHeaderLabel = (hour) => {
    const nextHour = hour + 1 === 24 ? '00' : hour + 1;
    return `${hour}-${nextHour}`;
};

// --- CALCOLO DATI ---
const buildOptimizedRows = (inizio, fine, data) => {
    if (!inizio || !fine || !data) return [];

    const startObj = dayjs(inizio);
    const endObj = dayjs(fine);

    if (!startObj.isValid() || !endObj.isValid()) return [];

    // Mappa dati: "YYYY-MM-DD" -> { "9": { total: Set(), bySede: { "Milano": Set(), "Roma": Set() } } }
    const attendanceMap = new Map();
    
    data.forEach(action => {
        if (!action.orario_inizio || !action.orario_fine || !action.contatto_id) return;
        
        const dateStr = dayjs(action.data).format('YYYY-MM-DD');
        const personId = action.contatto_id; 
        // Recuperiamo la sede (o "N/D" se manca)
        const sede = action.stakeholders?.sede || 'N/D';
        
        let startHour = parseInt(action.orario_inizio.split(':')[0], 10);
        let endHour = parseInt(action.orario_fine.split(':')[0], 10);
        const endMinutes = parseInt(action.orario_fine.split(':')[1], 10);

        if (endMinutes > 0) endHour += 1;
        if (endHour === 0 && startHour > 0) endHour = 24; 
        if (endHour < startHour) endHour += 24;

        if (!attendanceMap.has(dateStr)) {
            attendanceMap.set(dateStr, {}); 
        }
        
        const daySlots = attendanceMap.get(dateStr);
        
        for (let hour = startHour; hour < endHour; hour++) {
            const normalizedHour = hour >= 24 ? hour - 24 : hour;
            const hourKey = normalizedHour.toString();
            
            if (normalizedHour >= START_HOUR && normalizedHour <= END_HOUR) {
                // Inizializza l'oggetto per questa ora se non esiste
                if (!daySlots[hourKey]) {
                    daySlots[hourKey] = {
                        total: new Set(),
                        bySede: {}
                    };
                }

                // Aggiungi al totale globale dell'ora
                daySlots[hourKey].total.add(personId);

                // Aggiungi al conteggio specifico della sede
                if (!daySlots[hourKey].bySede[sede]) {
                    daySlots[hourKey].bySede[sede] = new Set();
                }
                daySlots[hourKey].bySede[sede].add(personId);
            }
        }
    });

    const rows = [];
    let currentDay = startObj;
    
    while (currentDay.isBefore(endObj) || currentDay.isSame(endObj, 'day')) {
        const dateStr = currentDay.format('YYYY-MM-DD');
        const dailySlots = attendanceMap.get(dateStr) || {};
        
        const row = {
            id: dateStr,
            giorno: currentDay.toDate(),
        };
        
        TIME_SLOTS.forEach(slot => {
            const slotKey = slot.toString();
            const slotData = dailySlots[slotKey];

            // 1. Valore numerico per la cella (Totale)
            row[slotKey] = slotData ? slotData.total.size : 0;

            // 2. Costruiamo la stringa per il Tooltip (Divisione Sede)
            if (slotData && slotData.total.size > 0) {
                // Esempio stringa: "Milano: 5\nRoma: 2"
                const detailString = Object.entries(slotData.bySede)
                    .map(([nomeSede, setIds]) => `${nomeSede}: ${setIds.size}`)
                    .join('\n'); // \n crea una nuova riga nel tooltip
                
                row[`${slotKey}_details`] = detailString;
            } else {
                row[`${slotKey}_details`] = '';
            }
        });
        
        rows.push(row);
        currentDay = currentDay.add(1, 'day');
    }
    
    return rows;
};

export default function ReportTableTimeSlots({ data, startDate, endDate }) {

    // 1. Calcolo dati
    const rows = useMemo(() =>
        buildOptimizedRows(startDate, endDate, data),
        [startDate, endDate, data]
    );

    // 2. Calcolo Massimo per Heatmap
    const maxAttendance = useMemo(() => {
        let max = 0;
        rows.forEach(row => {
            TIME_SLOTS.forEach(slot => {
                const val = row[slot.toString()] || 0;
                if (val > max) max = val;
            });
        });
        return max > 0 ? max : 1;
    }, [rows]);

    // 3. Helper Classi CSS
    const getHeatmapClass = (value, isTotalRow) => {
        if (!value || value === 0 || isTotalRow) return '';
        const percentage = value / maxAttendance;
        if (percentage >= 0.8) return 'heat-level-4'; 
        if (percentage >= 0.5) return 'heat-level-3'; 
        if (percentage >= 0.2) return 'heat-level-2'; 
        return 'heat-level-1';                          
    };

    // 4. Definizione Colonne
    const columns = [
        {
            field: 'giorno',
            headerName: 'Giorno',
            width: 120,
            valueFormatter: (params) => {
                if (params.value instanceof Date) return dayjs(params.value).format('DD/MM/YYYY');
                return params.value;
            },
            cellClassName: (params) => clsx('super-app', { total: params.row.id === "totale" }),
            pinned: 'left'
        },
        ...TIME_SLOTS.map(slot => ({
            field: slot.toString(),
            headerName: getHeaderLabel(slot),
            width: 70,
            type: 'number',
            align: 'center',
            headerAlign: 'center',
            cellClassName: (params) => {
                const isTotalRow = params.row.id === 'totale';
                const heatClass = getHeatmapClass(params.value, isTotalRow);
                return clsx('super-app', heatClass, { total: isTotalRow });
            },
            // USIAMO renderCell PER IL TOOLTIP
            renderCell: (params) => {
                const value = params.value;
                if (!value || value === 0) return "";

                // Recuperiamo i dettagli dalla proprietà nascosta creata in buildOptimizedRows
                const details = params.row[`${params.field}_details`];

                // Se è la riga totale, mostriamo solo il numero (o calcoliamo dettagli totali se servisse, ma è complesso)
                if (params.row.id === 'totale') {
                    return <span>{value}</span>;
                }

                return (
                    <Tooltip 
                        title={<div style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>{details}</div>} 
                        arrow
                        placement="top"
                    >
                        <div style={{ 
                            width: '100%', 
                            height: '100%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                        }}>
                            {value}
                        </div>
                    </Tooltip>
                );
            }
        }))
    ];

    // 5. Calcolo Riga Totali
    const totalRow = useMemo(() => {
        if (rows.length === 0) return null;
        const tRow = { id: 'totale', giorno: 'TOTALE' };
        TIME_SLOTS.forEach(slot => {
            const key = slot.toString();
            tRow[key] = rows.reduce((sum, row) => sum + (row[key] || 0), 0);
        });
        return tRow;
    }, [rows]);

    const finalRows = useMemo(() => {
         if (!totalRow) return rows;
         return [...rows, totalRow];
    }, [rows, totalRow]);

    return (
        <Box
            sx={{
                height: 600,
                width: '100%',
                '& .super-app': { transition: 'background-color 0.3s' },
                '& .super-app.total': { backgroundColor: 'rgba(0, 0, 0, 0.04) !important', fontWeight: 'bold', color: 'black !important' },
                '& .super-app.heat-level-1': { backgroundColor: '#e8f5e9', color: 'black' },
                '& .super-app.heat-level-2': { backgroundColor: '#a5d6a7', color: 'black' },
                '& .super-app.heat-level-3': { backgroundColor: '#66bb6a', color: 'black' },
                '& .super-app.heat-level-4': { backgroundColor: '#2e7d32', color: 'white', fontWeight: 'bold' },
            }}
        >
            <DataGrid
                rows={finalRows}
                columns={columns}
                getRowId={(row) => row.id}
                density="compact"
                slots={{
                    toolbar: GridToolbar,
                    noRowsOverlay: () => (
                        <Box p={2} display="flex" justifyContent="center" alignItems="center" height="100%">
                            <Typography>Seleziona un intervallo di date per vedere i dati.</Typography>
                        </Box>
                    )
                }}
                slotProps={{
                    toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } },
                }}
            />
        </Box>
    );
}